import { get, post, patch, del } from '../httpMethods';
import httpService from '../httpService';
import { ApiResponse, PaginationMeta } from '../../types/api';

export interface Bed {
  id: string;
  bedNumber: string;
  ward: string;
  wardId?: string;
  room?: string;
  status: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'MAINTENANCE';
  type: string;
  price?: number;
  createdAt: string;
}

export type AdmissionStatus = 'ADMITTED' | 'TRANSFERRED' | 'DISCHARGE_INITIATED' | 'DISCHARGED' | 'CANCELLED';

export interface Admission {
  id: string;
  patientId: string;
  visitId: string;
  /** Null when an emergency admission was created without a bed. */
  bedId?: string | null;
  doctorId: string;
  nurseId?: string;
  admissionType: string;
  status: AdmissionStatus;
  admittedAt: string;
  admissionReason?: string;
  referralDetails?: string;
  referredById?: string;
  /** Referral partner paid a flat commission on this admission. */
  referralId?: string | null;
  /** Flat referral commission for the whole stay (৳), not a percentage. */
  referralFlatAmount?: number;
  referral?: {
    id: string;
    name: string;
    code?: string;
    type?: 'DOCTOR' | 'EMPLOYEE' | 'OTHER';
    mobileNumber?: string;
    organization?: string;
    designation?: string;
    specialization?: string;
  } | null;
  dischargeInitiatedAt?: string;
  dischargedAt?: string;
  dischargeSummary?: string;
  dischargeInstructions?: string;
  dischargeType?: string;
  /** Unpaid balance carried at discharge (snapshot taken when the patient left). */
  dischargeDueAmount?: number;
  /** Optional promised payment date for the carried balance. */
  dischargeDueDate?: string | null;
  /** Optional note recorded with the carried balance. */
  dischargeDueNote?: string | null;
  advancePaid?: number;
  /**
   * Live billing state of the visit, resolved from its invoices plus any
   * charges that are still unbilled. PARTIAL means the invoices raised so far
   * are settled, but more charges are waiting to be invoiced.
   */
  paymentStatus?: 'PAID' | 'DUE' | 'PARTIAL' | 'NO_INVOICE' | 'RISK_FUND';
  /** Sum of the visit's non-cancelled invoice totals. */
  totalAmount?: number;
  /** Amount already collected on those invoices. */
  paidAmount?: number;
  /** Amount still owed on those invoices. */
  dueAmount?: number;
  invoiceCount?: number;
  /** Charges recorded on the visit that are not on an invoice yet. */
  unbilledCount?: number;
  /** Total value of those unbilled charges. */
  unbilledAmount?: number;
  /** When the visit's last invoice was settled; null while anything is due. */
  settledAt?: string | null;
  createdAt: string;
  /**
   * Last write on the record. Cancelling has no dedicated timestamp, so a
   * cancelled stay uses this as the moment it was closed.
   */
  updatedAt?: string;
  /** @deprecated Legacy field names kept for backwards compatibility. */
  diagnosis?: string;
  /** @deprecated Use `admittedAt` instead. */
  admissionDate?: string;
  patient?: any;
  bed?: any;
  doctor?: any;
  nurse?: any;
  referredBy?: any;
}

export interface AdmissionListFilters {
  search?: string;
  status?: string;
  admissionType?: string;
  gender?: string;
  bloodGroup?: string;
  wardId?: string;
  bedId?: string;
  patientId?: string;
  doctorId?: string;
  /**
   * Billing state of the visit: DUE (balance unpaid), PARTIAL (invoices settled
   * but charges still unbilled), RISK_FUND (balance written off), PAID (fully
   * invoiced and settled) or NO_INVOICE (nothing billed yet).
   */
  paymentStatus?: 'DUE' | 'PARTIAL' | 'PAID' | 'NO_INVOICE' | 'RISK_FUND';
  /** Due-tracking state: open / overdue / settled. */
  dueStatus?: 'open' | 'overdue' | 'settled';
  from?: string;
  to?: string;
  /** Which date `from`/`to` apply to: admitted (default) or settled on. */
  dateBasis?: 'admitted' | 'settled';
}

export interface BedListFilters {
  search?: string;
  status?: string;
  type?: string;
  wardId?: string;
}

export interface AdmissionCharge {
  id: string;
  patientId: string;
  visitId: string;
  serviceId: string;
  type: string;
  chargeDescription: string;
  unitPrice?: number;
  quantity?: number;
  unitLabel?: string;
  amount: number;
  tax: number;
  totalAmount: number;
  doctorId?: string;
  isInvoiced: boolean;
  invoiceId?: string;
  createdAt: string;
  service?: {
    id: string;
    name: string;
    code?: string;
    serviceType?: string;
    /** Catalog pricing fields — used to bound the quantity stepper. */
    price?: number;
    taxPercent?: number;
    billingUnit?: string;
    unitLabel?: string;
    minimumUnits?: number;
    unitIncrement?: number;
  } | null;
}

export interface AdmissionChargesResponse {
  admissionId: string;
  patientId: string;
  visitId: string;
  charges: AdmissionCharge[];
  totals: { unbilled: number; billed: number; total: number };
}

/** Result of re-checking a single admission's bed charges on demand. */
export interface AdmissionBedChargeRefreshResult {
  admissionId: string;
  isActive: boolean;
  postedNights: number;
  bedChargeThrough: string | null;
  bedChargeCount: number;
  billableThrough: string;
  reason: 'POSTED' | 'UP_TO_DATE' | 'NO_BED_PRICE' | 'NO_BED' | 'CLOSED';
}

export interface AdmissionBillingInvoice {
  id: string;
  invoiceNumber: string;
  status: string;
  total: number;
  paidAmount: number;
  dueAmount: number;
  createdAt: string;
}

/**
 * Billing clearance for an admission. `isCleared` is true only when the bill is
 * fully created (no unbilled charges) and fully paid (no outstanding due).
 */
export interface AdmissionBillingStatus {
  admissionId: string;
  visitId: string;
  patientId: string;
  unbilledCount: number;
  unbilledAmount: number;
  invoiceCount: number;
  unpaidInvoiceCount: number;
  outstandingDue: number;
  /** The visit's non-cancelled invoices, oldest first. */
  invoices: AdmissionBillingInvoice[];
  requiredAmount: number;
  /** True only when the bill is fully invoiced *and* fully paid. */
  isCleared: boolean;
  /**
   * True when every charge is on an invoice. From this point the patient can be
   * discharged even if a balance remains unpaid.
   */
  canDischarge: boolean;
  /** True when a balance would be carried if the patient were discharged now. */
  hasOutstandingDue: boolean;
  /** Advance collected at admission (informational; not applied to invoices). */
  advancePaid: number;
}

/** Payload accepted by the discharge endpoint. */
export interface DischargeAdmissionInput {
  /** Confirm leaving an unpaid balance on the patient's invoices. */
  allowDue?: boolean;
  dischargeDueDate?: string;
  dischargeDueNote?: string;
  dischargeSummary?: string;
  dischargeInstructions?: string;
  dischargeType?: string;
}

/** A service line added to an admission from the service cart. */
export interface AdmissionServiceInput {
  serviceId: string;
  quantity: number;
  /**
   * Overwritten price per unit. Only accepted for an Emergency service type on
   * an Emergency admission; omit it to bill the catalog price.
   */
  unitPrice?: number;
}

export interface CreateBedDto {
  bedNumber: string;
  ward: string;
  wardCode?: string;
  wardId?: string;
  type: 'GENERAL' | 'SEMI_PRIVATE' | 'PRIVATE' | 'ICU' | 'CCU' | 'NICU' | 'EMERGENCY';
  status?: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'MAINTENANCE';
  price?: number;
  description?: string;
  isActive?: boolean;
}

export type StaffAssignmentRole = 'PRIMARY_DOCTOR' | 'DUTY_DOCTOR' | 'NURSE' | 'SURGEON' | 'ANESTHESIOLOGIST' | 'CONSULTANT' | 'OTHER';

export interface StaffAssignmentDto {
  employeeId: string;
  role: StaffAssignmentRole;
  notes?: string;
}

export interface CreateAdmissionDto {
  patientId: string;
  /**
   * Optional source encounter (normally the active OPD visit). The server always
   * opens a dedicated IPD visit, so this is linked as the parent for continuity
   * and never receives the stay's charges.
   */
  visitId?: string;
  /** Required for direct/referral admissions; omitted for emergency admissions. */
  bedId?: string;
  doctorId: string;
  nurseId?: string;
  admissionType: string;
  admittedAt: string;
  admissionReason?: string;
  referralDetails?: string;
  referredById?: string;
  /** Referral partner paid a flat commission when the bill is fully settled. */
  referralId?: string | null;
  /** Flat referral commission for the whole admission (৳). */
  referralFlatAmount?: number;
  advancePaid?: number;
  /** Legacy id list: each service is billed once. Prefer `services`. */
  serviceIds?: string[];
  /** Quantity-aware service cart lines. */
  services?: AdmissionServiceInput[];
  staffAssignments?: StaffAssignmentDto[];
}

/**
 * Editable admission metadata. Patient, visit, bed and billable services are
 * intentionally excluded: bed moves use `/transfer`, and services are billed
 * through the charge/invoice pipeline.
 */
export interface UpdateAdmissionDto {
  /** `null` clears the primary doctor. */
  doctorId?: string | null;
  nurseId?: string;
  admissionType?: string;
  admittedAt?: string;
  admissionReason?: string;
  referralDetails?: string;
  /** `null` clears the referring doctor. */
  referredById?: string | null;
  /** `null` clears the referral partner and its flat amount. */
  referralId?: string | null;
  referralFlatAmount?: number;
  advancePaid?: number;
}

export const ipdService = {
  getBeds: (status?: string): Promise<ApiResponse<Bed[]>> => {
    const query = status ? `?status=${status}` : '';
    return get<Bed[]>(`/ipd/beds${query}`);
  },

  getBedsPaginated: (
    page = 1,
    limit = 20,
    filters?: BedListFilters,
  ): Promise<ApiResponse<Bed[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
    }
    return get<Bed[]>(`/ipd/beds?${params.toString()}`) as Promise<
      ApiResponse<Bed[]> & { meta: PaginationMeta }
    >;
  },

  getBed: (id: string): Promise<ApiResponse<Bed>> => {
    return get<Bed>(`/ipd/beds/${id}`);
  },

  createBed: (data: CreateBedDto): Promise<ApiResponse<Bed>> => {
    return post<Bed>('/ipd/beds', data);
  },

  updateBed: (id: string, data: Partial<CreateBedDto>): Promise<ApiResponse<Bed>> => {
    return patch<Bed>(`/ipd/beds/${id}`, data);
  },

  deleteBed: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/ipd/beds/${id}`);
  },

  bulkDeleteBeds: (
    ids: string[],
  ): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/ipd/beds/bulk-delete', { ids });
  },

  getAdmissions: (status?: string): Promise<ApiResponse<Admission[]>> => {
    const query = status ? `?status=${status}` : '';
    return get<Admission[]>(`/ipd/admissions${query}`);
  },

  getAdmissionsPaginated: (
    page = 1,
    limit = 20,
    filters?: AdmissionListFilters,
  ): Promise<ApiResponse<Admission[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
    }
    return get<Admission[]>(`/ipd/admissions?${params.toString()}`) as Promise<
      ApiResponse<Admission[]> & { meta: PaginationMeta }
    >;
  },

  getAdmission: (id: string): Promise<ApiResponse<Admission>> => {
    return get<Admission>(`/ipd/admissions/${id}`);
  },

  /** The patient's active (not-yet-discharged) admission, or null. */
  getActiveAdmissionByPatient: (patientId: string): Promise<ApiResponse<Admission | null>> => {
    return get<Admission | null>(`/ipd/patients/${patientId}/active-admission`);
  },

  bulkDeleteAdmissions: (
    ids: string[],
  ): Promise<
    ApiResponse<{ deleted: string[]; notFound: string[]; blocked: string[] }>
  > => {
    return post<{ deleted: string[]; notFound: string[]; blocked: string[] }>(
      '/ipd/admissions/bulk-delete',
      { ids },
    );
  },

  deleteAdmission: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/ipd/admissions/${id}`);
  },

  cancelAdmission: (id: string): Promise<ApiResponse<Admission>> => {
    return post<Admission>(`/ipd/admissions/${id}/cancel`, {});
  },

  createAdmission: (data: CreateAdmissionDto): Promise<ApiResponse<Admission>> => {
    return post<Admission>('/ipd/admissions', data);
  },

  /**
   * Update an admission's clinical/referral metadata. Bed moves, discharges and
   * service changes have dedicated endpoints and are not sent here.
   */
  updateAdmission: (
    id: string,
    data: UpdateAdmissionDto,
  ): Promise<ApiResponse<Admission>> => {
    return patch<Admission>(`/ipd/admissions/${id}`, data);
  },

  dischargeAdmission: (
    id: string,
    data: DischargeAdmissionInput,
  ): Promise<ApiResponse<Admission>> => {
    return post<Admission>(`/ipd/admissions/${id}/discharge`, data);
  },

  transferBed: (id: string, bedId: string): Promise<ApiResponse<Admission>> => {
    return post<Admission>(`/ipd/admissions/${id}/transfer`, { bedId });
  },

  getAdmissionCharges: (id: string): Promise<ApiResponse<AdmissionChargesResponse>> => {
    return get<AdmissionChargesResponse>(`/ipd/admissions/${id}/charges`);
  },

  /**
   * Posts any bed-charge nights still outstanding for this admission (idempotent).
   * Used by the Refresh action to re-check a bed charge that has not appeared yet.
   */
  refreshAdmissionBedCharges: (
    id: string,
  ): Promise<ApiResponse<AdmissionBedChargeRefreshResult>> => {
    return post<AdmissionBedChargeRefreshResult>(`/ipd/admissions/${id}/bed-charges/refresh`, {});
  },

  getAdmissionBillingStatus: (id: string): Promise<ApiResponse<AdmissionBillingStatus>> => {
    return get<AdmissionBillingStatus>(`/ipd/admissions/${id}/billing-status`);
  },

  /**
   * Fetch the admission payment receipt as a PDF object URL. The PDF is rendered
   * server-side, so it never includes the browser's own print header/footer.
   */
  fetchAdmissionReceiptPdfUrl: async (id: string): Promise<string> => {
    const response = await httpService.get(`/ipd/admissions/${id}/receipt/pdf`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  },

  addAdmissionServices: (id: string, items: AdmissionServiceInput[]): Promise<ApiResponse<AdmissionCharge[]>> => {
    return post<AdmissionCharge[]>(`/ipd/admissions/${id}/services`, { items });
  },

  /**
   * Change the quantity of an unbilled charge. The API re-prices the line from
   * the service catalog and adjusts the patient ledger by the difference.
   */
  updateAdmissionChargeQuantity: (
    admissionId: string,
    chargeId: string,
    quantity: number,
  ): Promise<ApiResponse<AdmissionCharge>> => {
    return patch<AdmissionCharge>(
      `/ipd/admissions/${admissionId}/charges/${chargeId}`,
      { quantity },
    );
  },

  /**
   * Remove an unbilled charge from an admission's visit. Charges already on an
   * invoice are refused — delete the invoice first, which releases its charges.
   */
  deleteAdmissionCharge: (
    admissionId: string,
    chargeId: string,
  ): Promise<ApiResponse<{ deleted: AdmissionCharge }>> => {
    return del<{ deleted: AdmissionCharge }>(
      `/ipd/admissions/${admissionId}/charges/${chargeId}`,
    );
  },

  /** Bulk-remove unbilled charges; blocked ids are reported back, not deleted. */
  bulkDeleteAdmissionCharges: (
    admissionId: string,
    ids: string[],
  ): Promise<
    ApiResponse<{ deleted: AdmissionCharge[]; blocked: string[]; notFound: string[] }>
  > => {
    return post<{ deleted: AdmissionCharge[]; blocked: string[]; notFound: string[] }>(
      `/ipd/admissions/${admissionId}/charges/bulk-delete`,
      { ids },
    );
  },

  getStaffAssignments: (admissionId: string): Promise<ApiResponse<any[]>> => {
    return get<any[]>(`/ipd/admissions/${admissionId}/staff`);
  },

  assignStaff: (admissionId: string, data: StaffAssignmentDto): Promise<ApiResponse<any>> => {
    return post<any>(`/ipd/admissions/${admissionId}/staff`, data);
  },

  removeStaffAssignment: (admissionId: string, assignmentId: string): Promise<ApiResponse<void>> => {
    return del<void>(`/ipd/admissions/${admissionId}/staff/${assignmentId}`);
  },
};
